import { dom, GridSettings, renderers } from "handsontable";
import { rowType, Rowprop } from "./rowProp";
import { IYieldDay } from "../../models/yield.models";
import { gestRateCellContent } from "./cellformater/restrictions";
import { stat as gnStat, day as gnDay } from "gn-shared";
import { store } from "redux/store";
import { format, getDay, startOfDay } from "date-fns";
import { fillCaption } from "./cellformater/rateCaptions";
import { YieldApi } from "../../api/yield";
import { toast } from "react-toastify";
import { addOutOfRange } from "../../redux/Slice/yieldssheetSlice";
import { ACTIONS, hasPermission } from "App/common/permissionHandler";

const rowArray: number[] = [];

export function computeOtaValues(
  row: any,
  rowProps: any,
  compsetType: string,
  compset: any,
  col: any,
) {
  const r = rowProps.get(row);
  const compsetMessage = compset[col][r.hotelName]
    ? compset[col][r.hotelName][compsetType + "Message"]
    : undefined;
  const compsetV = compset[col][r.hotelName]
    ? compset[col][r.hotelName][compsetType]
    : undefined;
  let compsetMessageDisplay = "-";

  switch (compsetMessage) {
    case "general.missing":
      compsetMessageDisplay = "-";
      break;
    case "rates.soldout":
      compsetMessageDisplay = "Sold Out";
      break;
    case "rates.nobar":
      compsetMessageDisplay = "No BAR";
      break;
    case "rates.meal.missing.1":
      compsetMessageDisplay = "No BB";
      break;
    case "rates.meal.missing.5":
      compsetMessageDisplay = "No RO";
      break;
    case "rates.restrictionlos2":
      compsetMessageDisplay = "LOS2";
      break;
    case "rates.restrictionlos3":
      compsetMessageDisplay = "LOS3";
      break;
    case "rates.no_person.1":
      compsetMessageDisplay = "No 1 pax";
      break;
    case "rates.no_person.2":
      compsetMessageDisplay = "No 2 pax";
      break;
    case "rates.onlythirdparty":
      compsetMessageDisplay = "3rd party";
      break;
    default:
      compsetMessageDisplay = "Sold Out*";
      break;
  }

  const t =
    typeof compsetV != "undefined"
      ? compsetV == 0
        ? compsetMessageDisplay
        : compsetV
      : "-";

  return t;
}

export function colWidths(daysCt: number): number[] {
  const firstColWidth = 250;
  return [firstColWidth, ...new Array(daysCt).fill(115)];
}

export function applyCellPropertiesClassName(cellProperties: any): string {
  return typeof cellProperties.className === "object"
    ? cellProperties.className.join(" ")
    : cellProperties.className || "";
}

function isReadonly(rowProp: any, hotel: any, permissions: any) {
  if (rowProp.isReadOnly) {
    return true;
  }
  if (hasPermission(ACTIONS.YIELD_SHEET)) {
    switch (rowProp.type) {
      case rowType.leftToSell:
        return false;
      case rowType.ratePlan:
        return rowProp.isManual ? false : true;
      default:
        return false;
    }
  } else if (hasPermission(ACTIONS.SUGGESTION)) {
    switch (rowProp.type) {
      case rowType.suggestedBaseRate:
        return false;
      case rowType.leftToSellUpdate:
        return false;
      default:
        return true;
    }
  } else {
    return true;
  }
}

function getClassToApply(
  col: number,
  rowProp: any,
  isBeforeToday: boolean,
  isEdited: boolean,
  isManualRPEdited: boolean,
  isWeekend: boolean,
  hotel: any,
) {
  let classes = "no-wrap";
  const rowtype = rowProp.type;
  if (isWeekend) {
    classes += " weekend";
  }
  if (col <= 0) {
    classes += " black strong";
  }
  switch (rowtype) {
    case rowType.suggestedBaseRate:
      classes += " focus suggested-rate rate";
      break;
    case rowType.actualOWSBaseRate:
      classes = " inverse";
      break;
    case rowType.actualBaseRate:
      classes = " inverse";
      if (rowProp.hasManualRP) {
        if (isManualRPEdited) {
          classes += col === 0 ? "" : " manualinverse";
        } else {
          classes += col === 0 ? "" : " manual";
        }
      }
      break;
    case rowType.occupancy:
    case rowType.ota:
      classes += " neutralblue";
      break;
    case rowType.marketDemand:
      classes += " marketDemand";
      break;
    case rowType.ratePlan:
      classes += " rate";
      break;
    case rowType.roomType:
      if ("isPublishingInventory" in hotel.meta) {
        if (hotel.meta.isPublishingInventory === true) {
          classes += " availibility";
        }
      }
      break;
    case rowType.pickup:
      classes += " pickup";
      break;
    case rowType.pickupFrom:
      classes += " pickupFrom";
      break;
    case rowType.leftToSell:
      classes += " leftToSell";
      break;
  }
  // Debug: log row/cell info for OWS Golden Years 2BB1D and Hens Package 2 Night
  if (
    rowProp &&
    rowProp.caption &&
    (rowProp.caption.includes("Golden Years 2BB1D") ||
      rowProp.caption.includes("Hens Package 2 Night"))
  ) {
    console.log(
      "[DEBUG] col:",
      col,
      "caption:",
      rowProp.caption,
      "rowProp:",
      rowProp,
      "hotel:",
      hotel,
    );
  }
  // Dim if isManual is undefined or false AND rowType is ratePlan
  if (
    rowProp &&
    rowProp.type === rowType.ratePlan &&
    (rowProp.isManual === undefined || rowProp.isManual === false)
  ) {
    classes += " locked";
  }
  // Only dim if explicitly false
  if (isEdited) {
    classes += " edited";
  }
  if (isBeforeToday) {
    classes += " disabled";
  }

  return classes;
}

export function cells(
  hotel: any,
  permissions: any,
  rowProps: Rowprop,
  sheet: IYieldDay[],
) {
  return (row: number, col?: number, prop?: object): GridSettings => {
    if (
      [row, col, prop].some((attr) => attr === undefined) ||
      col === undefined ||
      col - 1 >= sheet.length
    ) {
      return {};
    }

    const rowProp = rowProps.get(row);
    const day = sheet[col - 1];
    let isWeekend = false;

    if (col > 0) {
      const dow = getDay(new Date(day.hrDate));
      if (dow === 5 || dow === 6) {
        isWeekend = true;
      }
    }
    const cellProperties: GridSettings = {};
    cellProperties.readOnly = true;
    if (col > 0) {
      cellProperties.readOnly =
        isBeforeToday(day) || isReadonly(rowProp, hotel, permissions);
    }
    cellProperties.className = getClassToApply(
      col,
      rowProp,
      isBeforeToday(day),
      isEdited(rowProp, day),
      isManualRPEdited(day),
      isWeekend,
      hotel,
    );
    return cellProperties;
  };
}

function isManualRPEdited(day: IYieldDay): boolean {
  if (!day) {
    return false;
  }
  return Object.keys(gnDay.getUnpublishedManualRates(day)).length > 0;
}

function isEdited(rowProp: any, day: IYieldDay): boolean {
  if (!day) {
    return false;
  }
  switch (rowProp.type) {
    case rowType.ratePlan:
    case rowType.actualBaseRate:
    case rowType.actualOWSBaseRate:
      if (rowProp.isManual) {
        if (
          day.manualRates &&
          day.manualRates[rowProp.roomTypeId] &&
          day.manualRates[rowProp.roomTypeId]?.[rowProp.ratePlanId] &&
          day.manualRates[rowProp.roomTypeId]?.[rowProp.ratePlanId].stat ==
            gnStat.edited
        ) {
          return true;
        }
      }
      return day.stat === gnStat.edited;
    case rowType.roomType:
      return (
        (typeof day.availabilities[rowProp.roomTypeId] != "undefined"
          ? day.availabilities[rowProp.roomTypeId].stat
          : "-") == gnStat.edited
      );
    default:
      return false;
  }
}

function getAcceptButton(
  instance: any,
  row: number,
  col: number,
  value: any,
  day: any,
  forceRender: () => void,
) {
  const button = document.createElement("button");
  button.innerText = "✔";
  button.title = "Accept change";
  button.classList.add("btn", "btn-sm", "btn-success", "apply-btn");
  button.id = "acceptButton";
  button.addEventListener("click", (event) => {
    {
      const rateData = {
        hotelId: day.hotelId,
        start: day.hrDate,
        end: day.hrDate,
        newBaseRate: day.suggestedBaseRate,
        previousBaseRate: day.actualBaseRate,
      };
      YieldApi.acceptSuggestedRate(rateData)
        .catch(() => {
          toast.error("Error while accepting suggested base rates");
        })
        .finally(() => {
          forceRender();
        });
    }
    // instance.setDataAtCell(row, col, value);
    (event.target as HTMLButtonElement).disabled = true;
    event.stopPropagation();
    event.preventDefault();
  });

  return button;
}

function getDeclineButton(day: any, forceRender: any) {
  const button = document.createElement("button");
  button.innerText = "✖";
  button.title = "Decline change";
  button.classList.add("btn", "btn-sm", "btn-danger", "apply-btn");
  button.addEventListener("click", (event) => {
    {
      const rateData = {
        hotelId: day.hotelId,
        start: day.hrDate,
        end: day.hrDate,
        previousSuggestedRate: day.suggestedBaseRate,
      };
      YieldApi.removeSuggestedRate(rateData)
        .catch(() => {
          toast.error("Error while declining suggested base rates");
        })
        .finally(() => {
          forceRender();
        });
    }
    // instance.setDataAtCell(row, col, value);
    (event.target as HTMLButtonElement).disabled = true;
    event.stopPropagation();
    event.preventDefault();
  });
  return button;
}

export function columns(
  displayRange: any,
  hotel: any,
  sheet: IYieldDay[],
  compset: any[],
  compareOtaRate: any[],
  compareDemand: any[],
  rowProps: Rowprop,
  openSuggestionModalFunc: () => void,
  forceRender: () => void,
  computeLeftToSell: (e: any) => void,
  isOWSEnabled: boolean,
) {
  if (sheet.length === 0) {
    return null;
  }
  const marketDemandPickup = rowProps.marketDemand;
  const renderer: renderers.Base = (
    instance,
    TD,
    row,
    col,
    prop,
    value,
    cellProperties,
  ) => {
    dom.empty(TD);

    const rowProp = rowProps.get(row);
    const rowtype = rowProp.type;
    if (col === 0) {
      filterRow(instance, row, rowProp, value, displayRange);
      return fillCaption(
        row,
        value,
        sheet.length,
        prop,
        TD,
        cellProperties,
        rowProps,
        hotel.suggestionParams.isEnabled,
        hotel,
        instance,
        compset,
        compareOtaRate,
        compareDemand,
        hotel.hotelOnboardDate,
        openSuggestionModalFunc,
        forceRender,
        computeLeftToSell,
        isOWSEnabled, // <-- pass as last argument
      );
    }

    const day = sheet[col - 1];
    if (!day) {
      renderers.TextRenderer.apply(null, [
        instance,
        TD,
        row,
        col,
        prop,
        value,
        cellProperties,
      ]);
      return TD;
    }

    if (rowtype == rowType.suggestedBaseRate) {
      if (day.suggestedBaseRate !== undefined) {
        console.log("log data", gnStat, day);
        const div = document.createElement("div");
        div.innerText = `${value}`;
        //create the buttons
        if (hasPermission(ACTIONS.YIELD_SHEET)) {
          const acceptButton = getAcceptButton(
            instance,
            row,
            col,
            value,
            day,
            forceRender,
          );
          const declineButton = getDeclineButton(day, forceRender);
          div.appendChild(acceptButton);
          div.appendChild(declineButton);
        }
        TD.appendChild(div);
        TD.className = applyCellPropertiesClassName(cellProperties);
        return TD;
      }
    }
    let formulaLabel;
    if (typeof hotel.cm.channelConfig.ows.label != "undefined") {
      if (hotel.cm.channelConfig.ows.label != "") {
        formulaLabel = hotel.cm.channelConfig.ows.label;
      } else {
        formulaLabel = "Default";
      }
    } else {
      formulaLabel = "Default";
    }
    if (
      rowtype === rowType.actualOWSBaseRate &&
      day.OWSoveride &&
      day.OWSoveride == "Manual"
    ) {
      const s = document.createElement("span");
      s.innerText = "" + day.actualOWSBaseRate;
      TD.append(s);
      const tooltip = document.createElement("span");
      tooltip.innerText = day.OWSoveride;
      tooltip.className = "overidetooltipManual";
      s.appendChild(tooltip);

      TD.className =
        day.stat == gnStat.edited ? "overridenManualEdited" : "overridenManual";
      return TD;
    } else if (
      rowtype === rowType.actualOWSBaseRate &&
      day.OWSoveride &&
      day.OWSoveride != formulaLabel &&
      day.OWSoveride != "Default"
    ) {
      const s = document.createElement("span");
      s.innerText = "" + day.actualOWSBaseRate;
      TD.append(s);
      const tooltip = document.createElement("span");
      tooltip.innerText = day.OWSoveride;
      tooltip.className = "overidetooltip";
      s.appendChild(tooltip);

      TD.className +=
        day.stat == gnStat.edited ? "overridenEdited" : "overriden";
      return TD;
    }
    if (rowtype == rowType.marketDemand) {
      const s = document.createElement("div");
      s.innerHTML = `${value}`;

      const compareDemandPercentage = Math.round(
        Number(compareDemand[col]) * 100,
      );

      const withoutPer = value.replace("%", "");

      const calculatedDemand: number =
        Number(withoutPer) - Number(compareDemandPercentage);

      const tooltip = document.createElement("span");

      if (
        calculatedDemand === 0 ||
        value === "-" ||
        compareDemand[col] === "-"
      ) {
        tooltip.innerHTML = "";
      } else if (Number(withoutPer) > compareDemandPercentage) {
        tooltip.innerHTML = `+${calculatedDemand}`;
        tooltip.className = "otaCompareDataMore";
      } else {
        tooltip.innerHTML = `${calculatedDemand}`;
        tooltip.className = "otaCompareDataLess";
      }

      s.appendChild(tooltip);
      TD.append(s);
      TD.className = "marketDemand";
      return TD;
    }

    if (rowtype === rowType.ota) {
      const r = rowProps.get(row);
      const otaRoomName = compset[col][r.hotelName]
        ? compset[col][r.hotelName][rowProps.defaultCompsetType + "RoomName"]
        : undefined;

      const compsetV = compset[col][r.hotelName]
        ? compset[col][r.hotelName][rowProps.defaultCompsetType]
        : undefined;

      const compareOtaRates = compareOtaRate[col][r.hotelName]
        ? compareOtaRate[col][r.hotelName][rowProps.defaultCompsetType]
        : undefined;

      const compareOtaRoomName = compareOtaRate[col][r.hotelName]
        ? compareOtaRate[col][r.hotelName][
            rowProps.defaultCompsetType + "RoomName"
          ]
        : undefined;

      const otaRoomNameAndRoomType = `${otaRoomName}`;

      const compareOtaRoomNameAndRoomType = `${compareOtaRoomName}`;

      const t = computeOtaValues(
        row,
        rowProps,
        rowProps.defaultCompsetType,
        compset,
        col,
      );
      const s = document.createElement("h6");
      s.id = `otarate_${row}_${col}`;
      s.className = "otaSpanClass";
      s.innerText = t;

      let ans = compsetV - compareOtaRates;
      ans = parseFloat(ans.toFixed(2));

      TD.append(s);

      const tooltip = document.createElement("span");
      const flag = document.createElement("div");

      const hoverText = document.createElement("div");
      const compareRoomNameDiv = document.createElement("div");
      compareRoomNameDiv.className = "compareRoomNameDiv";
      const currentRoomNameElement = document.createElement("span");
      const prevRoomNameElement = document.createElement("span");
      const roomAndRateType = document.createElement("span");

      tooltip.innerHTML = ans.toString();
      hoverText.className = "prevTooltip";

      if (col == 1) {
        rowArray.push(row);
      }

      const lastCol = rowArray[hotel.ota.compSet.length - 1];
      const secondLast = rowArray[hotel.ota.compSet.length - 2];
      const thirdLast = rowArray[hotel.ota.compSet.length - 3];

      if (row == lastCol || row == secondLast || row == thirdLast) {
        compareRoomNameDiv.className = "compareRoomNameDiv";
        currentRoomNameElement.className = "upTooltip";
        prevRoomNameElement.className = "upTooltip";
        roomAndRateType.className = "singleRoomNameEx";
      } else {
        compareRoomNameDiv.className = "";
        currentRoomNameElement.className = "orignalTooltip";
        prevRoomNameElement.className = "orignalTooltip";
        roomAndRateType.className = "roomToolTip";
      }

      const compareDate = format(new Date(marketDemandPickup), "dd/MM");

      if (compsetV == 0 || compsetV === undefined) {
        currentRoomNameElement.innerText = "";
        prevRoomNameElement.innerText = "";
        roomAndRateType.innerHTML = "";
      } else if (compareOtaRates == 0 || compareOtaRates == undefined) {
        roomAndRateType.innerHTML = otaRoomName ? `${otaRoomName}` : "NA";
        currentRoomNameElement.innerText = "";
        prevRoomNameElement.innerText = "";
        // roomAndRateType.className = "roomToolTip";
      } else if (otaRoomNameAndRoomType !== compareOtaRoomNameAndRoomType) {
        flag.className = "otaRowFlag";
        flag.innerHTML = "&#9666";
        currentRoomNameElement.innerHTML = `&#8226; Today: ${compsetV} &nbsp&nbsp&nbsp - ${otaRoomNameAndRoomType}`;
        prevRoomNameElement.innerHTML = `&#8226; ${compareDate}: ${compareOtaRates} &nbsp&nbsp&nbsp - ${compareOtaRoomNameAndRoomType}`;
      } else {
        roomAndRateType.innerHTML = otaRoomName ? `${otaRoomName}` : "NA";
      }

      if (ans < 0) {
        tooltip.className = "otaCompareDataLess";
        tooltip.innerHTML = ans.toString();
      } else if (
        ans == 0 ||
        compareOtaRates == 0 ||
        compareOtaRates == undefined
      ) {
        tooltip.innerHTML = "";
      } else if (compsetV === undefined) {
        tooltip.innerHTML = "";
      } else {
        tooltip.className = "otaCompareDataMore";
        tooltip.innerHTML = `+${ans.toString()}`;
      }

      s.appendChild(flag);
      s.appendChild(hoverText);

      hoverText.appendChild(compareRoomNameDiv);
      compareRoomNameDiv.appendChild(currentRoomNameElement);
      compareRoomNameDiv.appendChild(prevRoomNameElement);
      hoverText.appendChild(roomAndRateType);

      TD.className = "otaRow";
      return TD;
    }

    if (
      rowtype === rowType.pickupFrom &&
      typeof value === "string" &&
      value.includes(";")
    ) {
      const [diffStr, tsStr] = value.split(";");
      const diffNum = Number(diffStr);
      const ts = Number(tsStr);

      const s = document.createElement("div");
      s.className = "otaSpanClass";
      s.innerHTML = isNaN(diffNum) ? "-" : diffNum.toString();

      const flag = document.createElement("div");
      flag.className = "otaRowFlag";
      flag.innerHTML = "&#9666";

      const hoverText = document.createElement("div");
      hoverText.className = "prevTooltip";
      const line = document.createElement("div");
      const label = document.createElement("span");
      const val = document.createElement("span");
      label.className = "orignalTooltip";
      val.className = "roomToolTip";
      label.innerHTML = "Pickup date:";
      val.innerHTML = ` ${format(new Date(ts), "d MMM yyyy")}`;
      line.appendChild(label);
      line.appendChild(val);
      hoverText.appendChild(line);

      s.appendChild(flag);
      s.appendChild(hoverText);

      TD.appendChild(s);
      const baseClass = applyCellPropertiesClassName(cellProperties);
      TD.className = `${baseClass} otaRow`;
      return TD;
    }

    const restrictionContent = gestRateCellContent(
      rowProps,
      rowProp,
      day,
      value,
      cellProperties,
    );
    if (restrictionContent) {
      TD.className = applyCellPropertiesClassName(cellProperties);
      TD.appendChild(restrictionContent);
    } else {
      if (value && typeof value == "string" && value.indexOf(";") !== -1) {
        const t = value.split(";");
        value = t[0];
      }
      renderers.TextRenderer.apply(null, [
        instance,
        TD,
        row,
        col,
        prop,
        value,
        cellProperties,
      ]);
    }

    return TD;
  };
  return () => ({ renderer });
}

export function getRooms(sheet: IYieldDay[]): string[] {
  if (!sheet.length) {
    return [];
  }
  const day = sheet[0];
  return Object.keys(day.derivedRates);
}

export function hasOWSBRRow(hotel: any) {
  return hotel.cm.channelConfig && hotel.cm.channelConfig.ows;
}

// REV-261
export function availabilityExceedsTotalRooms(
  totalRooms: string,
  value: number,
): boolean | null {
  return Number(totalRooms) ? Number(totalRooms) < value : null;
}

export function filterRow(
  instance: any,
  row: number,
  rowProp: any,
  rowName: string,
  displayRange: any,
) {
  if (!rowName) {
    return;
  }
  if (rowProp.type != rowType.ratePlan) {
    return;
  }

  if (rowProp.autoCloseOut) {
    if (
      (displayRange.start &&
        displayRange.end &&
        rowProp.autoCloseOut.start > displayRange.end) ||
      rowProp.autoCloseOut.end < displayRange.start
    ) {
      store.dispatch(addOutOfRange(rowProp.ratePlanId));
      const plugin = instance.getPlugin("hiddenRows");
      plugin.hideRows([row]);
    }
  }
}

export function isBeforeToday(day: IYieldDay) {
  if (!day) {
    return false;
  }
  if (startOfDay(new Date()).valueOf() > day.dateMidday) {
    return true;
  }
  return false;
}
